import { FC, useState, useEffect } from "react"
import { NavLink } from "react-router-dom"
import { cn } from "@/lib/utils"
import {
	LayoutGrid,
	Folders,
	Settings,
	ChevronRight,
	LogOut,
	ChevronLeft,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { auth } from "@/lib/firebase"
import { signOut } from "firebase/auth"
import { useNavigate } from "react-router-dom"

export const Sidebar: FC = () => {
	const [isCollapsed, setIsCollapsed] = useState(false)
	const [textOpacityClass, setTextOpacityClass] = useState("opacity-0")
	const navigate = useNavigate()

	useEffect(() => {
		let timer: NodeJS.Timeout
		if (!isCollapsed) {
			// Start with opacity-0, then transition to opacity-100 after a delay
			setTextOpacityClass("opacity-0") // Ensure it's hidden when it first renders or !isCollapsed becomes true
			timer = setTimeout(() => {
				setTextOpacityClass("opacity-100")
			}, 150) // 150ms delay
		} else {
			setTextOpacityClass("opacity-0") // Hide immediately on collapse
		}
		return () => clearTimeout(timer)
	}, [isCollapsed])

	const toggleSidebar = () => {
		setIsCollapsed(!isCollapsed)
	}

	const handleLogout = async () => {
		try {
			await signOut(auth)
			navigate("/login")
		} catch (error) {
			console.error("Error signing out:", error)
		}
	}

	return (
		<aside
			className={cn(
				"bg-white border-r border-border transition-all duration-300 ease-apple-ease",
				isCollapsed ? "w-16" : "w-64"
			)}
		>
			<div className="flex flex-col h-full">
				<div className="flex items-center justify-between h-16 px-4 border-b">
					<div className="w-full flex flex-row justify-start items-center gap-3">
						<img
							src="/logo.png"
							alt="Expert Neurological Case Management"
							className={cn(
								"flex-shrink-0 animate-in duration-300",
								isCollapsed ? "h-8 w-8" : "h-10 w-10"
							)}
						/>
						{!isCollapsed && (
							<div
								className={`flex flex-col transition-opacity duration-500 ${textOpacityClass}`}
							>
								<h1 className="text-lg font-semibold text-foreground leading-tight">
									Expert Neurological
								</h1>
								<h2 className="text-sm font-medium text-foreground/70 leading-tight">
									Case Management
								</h2>
							</div>
						)}
					</div>
					<Button
						variant="ghost"
						size="icon"
						onClick={toggleSidebar}
						className={cn(
							"rounded-full h-8 w-8 animate-in slide-in-from-right-2 duration-300",
							isCollapsed && "absolute top-4 right-2 opacity-0"
						)}
					>
						<ChevronLeft className="h-5 w-5" />
					</Button>
				</div>
				<div
					className={cn(
						"flex justify-center px-2 border-b transition-all duration-300 ease-apple-ease overflow-hidden",
						isCollapsed ? "py-2 opacity-100 max-h-12" : "py-0 opacity-0 max-h-0"
					)}
				>
					<Button
						variant="ghost"
						size="icon"
						onClick={toggleSidebar}
						className="rounded-full h-8 w-8"
					>
						<ChevronRight className="h-5 w-5" />
					</Button>
				</div>
				<nav
					className={cn(
						"flex-1 px-2 py-4 space-y-1 transition-all duration-300",
						!isCollapsed && "animate-in slide-in-from-left-2 duration-300",
						isCollapsed && "animate-in slide-in-from-top-4 duration-300"
					)}
				>
					<NavItem
						to="/"
						icon={<LayoutGrid className="h-5 w-5" />}
						label="Dashboard"
						isCollapsed={isCollapsed}
					/>
					<NavItem
						to="/cases"
						icon={<Folders className="h-5 w-5" />}
						label="Cases"
						isCollapsed={isCollapsed}
					/>
				</nav>
				<div className="p-4 border-t">
					<NavItem
						to="/settings"
						icon={<Settings className="h-5 w-5" />}
						label="Settings"
						isCollapsed={isCollapsed}
					/>
					<div className="mt-1">
						<button
							onClick={handleLogout}
							className={cn(
								"flex items-center w-full px-3 py-2 rounded-lg transition-all group",
								"text-foreground/70 hover:bg-red-100 hover:text-red-600"
							)}
						>
							<div className="flex items-center">
								<span className="flex-shrink-0">
									<LogOut className="h-5 w-5" />
								</span>
								{!isCollapsed && (
									<span className="ml-3 text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap">
										Logout
									</span>
								)}
							</div>
						</button>
					</div>
				</div>
			</div>
		</aside>
	)
}

interface NavItemProps {
	to: string
	icon: React.ReactNode
	label: string
	isCollapsed: boolean
}

const NavItem: FC<NavItemProps> = ({ to, icon, label, isCollapsed }) => {
	return (
		<NavLink
			to={to}
			className={({ isActive }) =>
				cn(
					"flex items-center px-3 py-2 rounded-lg transition-all group",
					isActive
						? "bg-primary text-primary-foreground"
						: "text-foreground/70 hover:bg-secondary hover:text-foreground"
				)
			}
		>
			<div className="flex items-center">
				<span className="flex-shrink-0">{icon}</span>
				{!isCollapsed && (
					<span className="ml-3 text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap">
						{label}
					</span>
				)}
			</div>
		</NavLink>
	)
}
